import { useContext, useEffect } from 'react';

import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useRouter } from '@eneco/next';
import { GrowthBookContext } from '@growthbook/growthbook-react';

import { FlowConsumerContextEnergy } from '../../../../context';
import { Offer } from '../../../../shared/types';
import { touExperimentFeatureToggleKey, touExperimentProductFilter } from '../../../growthbook/experiment.helpers';

export const promoteTimeOfUseOfferFeatureKey = 'exp-n-657-promote-time-of-use-offer';
export const promoteTimeOfUseOfferVariant = 'B';

/** Promotion code shown when a visitor is kept out of the Time of Use experiment. */
export const excludedTouRecommendedOffer = 'POAA12CExxxxxC';

const touOffersConfig = {
  experimentKey: touExperimentFeatureToggleKey,
  filterPredicate: touExperimentProductFilter,
};

export type TouOffersConfig = {
  experimentKey: string;
  filterPredicate: (offer: Offer, variant: string | null) => boolean;
} | null;

/**
 * exp-n-657 — Time of Use offer filtering.
 *
 * Runs only for variant B of `exp-n-657-promote-time-of-use-offer`. In that variant, a visitor with
 * `showPromotionDialog` skips the Time of Use filter and stores {@link excludedTouRecommendedOffer}
 * on the flow context. Every other variant keeps the normal filter and leaves `recommendedOffer` unchanged.
 * The feature is only read when `showPromotionDialog` is set, so other visitors are not exposed to this
 * experiment and stay in the Time of Use filter. Skipping the filter also skips its `getFeatureValue`.
 * Delete this hook (and its call sites) to always apply the filter.
 */
export const useTouOffersConfig = (): TouOffersConfig => {
  const { query } = useRouter();
  const { growthbook } = useContext(GrowthBookContext);
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowConsumerContextEnergy>();
  const showPromotionDialog = useFlowSelector(state => state.context.showPromotionDialog);
  const recommendedOffer = useFlowSelector(state => state.context.recommendedOffer);
  const sendToFlowMachine = useFlowActorRef().send;

  const shouldShowPromotionDialog =
    Boolean(showPromotionDialog) &&
    query.disableExperiments !== 'true' &&
    growthbook?.getFeatureValue(promoteTimeOfUseOfferFeatureKey, null) === promoteTimeOfUseOfferVariant;

  useEffect(() => {
    if (!shouldShowPromotionDialog || recommendedOffer === excludedTouRecommendedOffer) return;

    sendToFlowMachine({
      type: 'UPDATE_VALUES',
      values: { recommendedOffer: excludedTouRecommendedOffer },
    });
  }, [shouldShowPromotionDialog, recommendedOffer, sendToFlowMachine]);

  if (shouldShowPromotionDialog) return null;

  return touOffersConfig;
};
